/**
 * NOTE that this mapping IS TEMPORARY. We need to handle this mapping as a
 * concern of products and resolve it via a graphcall not statically in code
 * like this.
 *
 * This was simply done for convenience. PLEASE DO NOT ADD THIS WITHOUT FIRST
 * CHECKING WITH DPI.
 *
 */
/**
 * Overflow-specific mapping between print MPV IDs and service MPV IDs.
 * This mapping is exclusively used for product overflow scenarios where
 * print products need to be redirected to alternative design services.
 */

export interface ServiceMpvMapping {
    printMpvId: string;
    serviceMpvId: string;
}

/**
 * Minimal typed subset of an ExpertService from the graph.
 * Used by helper functions that need to check overflow eligibility
 * without depending on the full generated ExpertService type.
 */
export interface OverflowExpertService {
    isOverflowEligible: boolean;
    serviceProduct: { mpvId: string };
}

/**
 * Supported locales for overflow service configuration.
 */
export const SUPPORTED_OVERFLOW_LOCALES = ['en-US', 'es-US', 'en-CA', 'fr-CA', 'en-AU', 'en-SG'];

/**
 * Maps print MPV IDs to their corresponding overflow service MPV IDs.
 * This mapping is used exclusively for product overflow scenarios:
 * - Determining which print products are eligible for overflow
 * - Redirecting overflow traffic to appropriate design services
 * - Building URLs with correct service MPV IDs and options
 */
export const OVERFLOW_PRODUCTS_MAPPING: ServiceMpvMapping[] = [
    // Flyers mapping
    { printMpvId: 'flyers', serviceMpvId: 'flyerdesign' },
    { printMpvId: 'flyersandLeaflets', serviceMpvId: 'flyerdesign' },
    { printMpvId: 'perforatedFlyers', serviceMpvId: 'flyerdesign' },

    // Banner design mappings
    { printMpvId: 'retractableBanners', serviceMpvId: 'bannerDesign' },
    { printMpvId: 'vinylBanners', serviceMpvId: 'bannerDesign' },
    { printMpvId: 'stepAndRepeatBanners', serviceMpvId: 'bannerDesign' },
    { printMpvId: 'fabricBanners', serviceMpvId: 'bannerDesign' },
    { printMpvId: 'customSizeVinylBanners', serviceMpvId: 'bannerDesign' },
    { printMpvId: 'doubleSidedBanners', serviceMpvId: 'bannerDesign' },
    { printMpvId: 'meshBanners', serviceMpvId: 'bannerDesign' },
    { printMpvId: 'pennantBanners', serviceMpvId: 'bannerDesign' },
    { printMpvId: 'poleBanners', serviceMpvId: 'bannerDesign' },

    // Business card design mappings
    { printMpvId: 'standardBusinessCards', serviceMpvId: 'businessCardDesign' },
    { printMpvId: 'matteBusinessCards', serviceMpvId: 'businessCardDesign' },
    { printMpvId: 'roundedCornerBusinessCards', serviceMpvId: 'businessCardDesign' },
    { printMpvId: 'glossyBusinessCards', serviceMpvId: 'businessCardDesign' },
    { printMpvId: 'raisedFoilBusinessCards', serviceMpvId: 'businessCardDesign' },
    { printMpvId: 'foilAccentBusinessCards', serviceMpvId: 'businessCardDesign' },
    { printMpvId: 'premiumPlusBusinessCards', serviceMpvId: 'businessCardDesign' },
    { printMpvId: 'bambooBusinessCards', serviceMpvId: 'businessCardDesign' },
    { printMpvId: 'cottonBusinessCards', serviceMpvId: 'businessCardDesign' },
    { printMpvId: 'embossedGlossBusinessCards', serviceMpvId: 'businessCardDesign' },
    { printMpvId: 'fineGritBusinessCards', serviceMpvId: 'businessCardDesign' },
    { printMpvId: 'frostedAndClearBusinessCards', serviceMpvId: 'businessCardDesign' },
    { printMpvId: 'hempBusinessCards', serviceMpvId: 'businessCardDesign' },
    { printMpvId: 'kraftBusinessCards', serviceMpvId: 'businessCardDesign' },
    { printMpvId: 'linenBusinessCards', serviceMpvId: 'businessCardDesign' },
    { printMpvId: 'naturalTexturedBusinessCards', serviceMpvId: 'businessCardDesign' },
    { printMpvId: 'paintedEdgeBusinessCards', serviceMpvId: 'businessCardDesign' },
    { printMpvId: 'pearlBusinessCards', serviceMpvId: 'businessCardDesign' },
    { printMpvId: 'plasticBusinessCards', serviceMpvId: 'businessCardDesign' },
    { printMpvId: 'recycledMatteBusinessCards', serviceMpvId: 'businessCardDesign' },
    { printMpvId: 'softTouchBusinessCards', serviceMpvId: 'businessCardDesign' },
    { printMpvId: 'ultraThickBusinessCards', serviceMpvId: 'businessCardDesign' },
    { printMpvId: 'uncoatedBusinessCards', serviceMpvId: 'businessCardDesign' },
    { printMpvId: 'whitePlasticBusinessCards', serviceMpvId: 'businessCardDesign' },

    // Postcard design mappings
    { printMpvId: 'postcards', serviceMpvId: 'postcardDesign' },
    { printMpvId: 'dieCutPostcards', serviceMpvId: 'postcardDesign' },
    { printMpvId: 'embossedFoilPostcards', serviceMpvId: 'postcardDesign' },
    { printMpvId: 'embossedFoilRackCards', serviceMpvId: 'postcardDesign' },
    { printMpvId: 'embossedGlossPostcards', serviceMpvId: 'postcardDesign' },
    { printMpvId: 'everyDoorDirectMailPostcards', serviceMpvId: 'postcardDesign' },
    { printMpvId: 'foilAccentPostcards', serviceMpvId: 'postcardDesign' },
    { printMpvId: 'mailingServicesPostcard', serviceMpvId: 'postcardDesign' },
    { printMpvId: 'postcardMagnets', serviceMpvId: 'postcardDesign' },
    { printMpvId: 'roundedCornerPostcards', serviceMpvId: 'postcardDesign' },

    // Brochure design mappings
    { printMpvId: 'bifoldbrochures', serviceMpvId: 'brochureDesign' },
    { printMpvId: 'biFoldMenus', serviceMpvId: 'brochureDesign' },
    { printMpvId: 'triFoldBrochures', serviceMpvId: 'brochureDesign' },
    { printMpvId: 'zFoldBrochures', serviceMpvId: 'brochureDesign' },
    { printMpvId: 'foldedLeaflets', serviceMpvId: 'brochureDesign' },

    // Door hanger design mappings
    { printMpvId: 'doorHanger', serviceMpvId: 'doorHangerDesign' },
    { printMpvId: 'doorHangers', serviceMpvId: 'doorHangerDesign' },

    // Poster design mappings
    { printMpvId: 'posters', serviceMpvId: 'posterDesign' },
    { printMpvId: 'outdoorPostersNa', serviceMpvId: 'posterDesign' },
    { printMpvId: 'siliconeEdgeGraphics', serviceMpvId: 'posterDesign' },
    { printMpvId: 'stickyPosters', serviceMpvId: 'posterDesign' },
    { printMpvId: 'wallMountedSiliconeEdgeGraphics', serviceMpvId: 'posterDesign' },
    { printMpvId: 'wallPosters', serviceMpvId: 'posterDesign' },

    // Yard sign design mappings
    { printMpvId: 'yardSigns', serviceMpvId: 'yardSignDesign' },
    { printMpvId: 'lawnSigns', serviceMpvId: 'yardSignDesign' },

    // Other design mappings
    { printMpvId: 'thankYouCards', serviceMpvId: 'otherDesign' },
];

/**
 * Gets the service MPV ID for a given print MPV ID.
 * Comparison is case-insensitive as per requirements.
 *
 * @param printMpvId - The print MPV ID to map
 * @returns The corresponding service MPV ID, or undefined if no mapping exists
 */
export function getServiceMpvId(printMpvId: string): string | undefined {
    const mapping = OVERFLOW_PRODUCTS_MAPPING.find(
        (item) => item.printMpvId.toLowerCase() === printMpvId.toLowerCase()
    );
    return mapping?.serviceMpvId;
}

/**
 * Gets all print MPV IDs that map to a specific service MPV ID.
 * Useful for reverse lookups and validation.
 *
 * @param serviceMpvId - The service MPV ID to find print MPV IDs for
 * @returns Array of print MPV IDs that map to the given service MPV ID
 */
export function getPrintMpvIdsForService(serviceMpvId: string): string[] {
    return OVERFLOW_PRODUCTS_MAPPING.filter(
        (item) => item.serviceMpvId.toLowerCase() === serviceMpvId.toLowerCase()
    ).map((item) => item.printMpvId);
}

/**
 * Gets all eligible service MPV IDs from the mapping.
 * This is useful for feature flag checks and validation.
 *
 * @returns Array of unique service MPV IDs
 */
export function getEligibleServiceMpvIds(): string[] {
    const serviceIds = OVERFLOW_PRODUCTS_MAPPING.map((item) => item.serviceMpvId);
    return [...new Set(serviceIds)];
}

/**
 * Gets all eligible print MPV IDs from the mapping.
 * This is useful for feature flag checks and validation.
 *
 * @returns Array of print MPV IDs
 */
export function getEligiblePrintMpvIds(): string[] {
    return OVERFLOW_PRODUCTS_MAPPING.map((item) => item.printMpvId);
}

/**
 * Checks if a print MPV ID is eligible for overflow feature flag.
 *
 * @param printMpvId - The print MPV ID to check
 * @returns True if the print MPV ID has a corresponding service mapping
 */
export function isPrintMpvIdEligible(printMpvId: string): boolean {
    return getServiceMpvId(printMpvId) !== undefined;
}

/**
 * Checks if a service MPV ID is eligible for overflow feature flag.
 *
 * @param serviceMpvId - The service MPV ID to check
 * @returns True if the service MPV ID exists in the mapping
 */
export function isServiceMpvIdEligible(serviceMpvId: string): boolean {
    return getEligibleServiceMpvIds().some((id) => id.toLowerCase() === serviceMpvId.toLowerCase());
}

export interface OverflowServiceConfig {
    esMpvId: string;
    designerGroupOption?: { key: string; value: string };
}

/**
 * Determines overflow service configuration for the Header component.
 * Uses the `isOverflowEligible` field from the graph to determine eligibility
 * instead of relying on a static mapping.
 * Only applies the overflow mapping for supported locales.
 *
 * @param expertService - The expert service object from graph data
 * @param locale - The current locale
 * @param reviewMethod - The selected review method (e.g. 'Standard', 'DesignLive')
 * @returns OverflowServiceConfig with esMpvId and designerGroupOption (if applicable), or null if not eligible
 */
export function getOverflowServiceConfig(
    expertService: OverflowExpertService | undefined,
    locale: string,
    reviewMethod: string
): OverflowServiceConfig | null {
    // Only apply overflow mapping for supported locales
    if (!SUPPORTED_OVERFLOW_LOCALES.includes(locale)) {
        return null;
    }

    if (reviewMethod !== 'Standard') {
        return null;
    }

    if (!expertService?.isOverflowEligible) {
        return null;
    }

    const config: OverflowServiceConfig = {
        esMpvId: expertService.serviceProduct.mpvId,
    };

    // Add designer group option if esMpvId !== caredesign (case insensitive)
    if (expertService.serviceProduct.mpvId.toLowerCase() !== 'caredesign') {
        config.designerGroupOption = {
            key: 'DesignerGroupOption',
            value: 'In-house',
        };
    }

    return config;
}
